import { css } from '@emotion/react';
import React, { useCallback, useEffect } from 'react';

import { timeAgo } from '@mythos/utils/dateUtils';
import { useNavigate } from 'react-router-dom';

import Session from '@mythos/utils/Session';
import invariant from 'invariant';
import { MEDIA_QUERY_MOBILE } from 'mobile_ui/MStyles';
import { useMobile } from 'MobileContext';
import { match, P } from 'ts-pattern';
import { useShallow } from 'zustand/shallow';
import { ColorPalette, UIColorPalette } from './ColorPalette';
import { graphql, useFragment } from './generated/gql';
import {
  executeGQL,
  LobbyListItem,
  UserSummaryFragmentDoc,
} from './GraphQLHelpers';
import LobbyButton from './LobbyButton';
import LobbyMutator from './LobbyMutator';
import { subscribeToTopic, unsubscribeFromTopic } from './stores/channelStore';
import { useLobbyStore } from './stores/lobbyStore';
import { useSessionStore } from './stores/sessionStore';
import UserHoverView from './UserHoverView';

const LobbyListEntry: React.FC<{
  lobby: LobbyListItem;
  sessionUserID?: string;
  onJoin: (lobby: LobbyListItem) => void;
  onSpectate: (lobby: LobbyListItem) => void;
}> = (props) => {
  const { sessionUserID, onJoin, onSpectate, lobby } = props;
  const game = lobby.game;

  let connect_section: React.ReactNode;
  if (game) {
    const contains_session_user = lobby.players?.edges?.some((edge: any) => {
      return edge.node.id === sessionUserID;
    });
    let button_text = contains_session_user ? 'Resume Game' : 'Spectate';
    if (game.endTimestamp) {
      button_text = 'View Results';
    }
    connect_section = (
      <LobbyButton secondary={true} onClick={() => onSpectate(lobby)}>
        {button_text}
      </LobbyButton>
    );
  } else {
    connect_section = (
      <LobbyButton onClick={() => onJoin(lobby)}>Join Lobby</LobbyButton>
    );
  }
  const Cell = (props: { className?: string; children: React.ReactNode }) => (
    <td className={props.className} css={LobbyListStyles.tableCell}>
      {props.children}
    </td>
  );
  const host = useFragment(UserSummaryFragmentDoc, lobby.host);
  return (
    <tr css={LobbyListStyles.tableRow}>
      <Cell>{lobby.name}</Cell>
      <Cell css={LobbyListStyles.tableCellHost}>
        <UserHoverView user={host as any}>
          <div>
            <img src={host.profilePictureURL} css={LobbyListStyles.profile} />
            <span css={LobbyListStyles.hostName}>{host.name}</span>
          </div>
        </UserHoverView>
      </Cell>
      <Cell css={LobbyListStyles.tableCellSmall}>
        {lobby.players?.edges?.length ?? 0}
      </Cell>
      <Cell css={LobbyListStyles.tableCellSmall}>
        {timeAgo(lobby.creationDate)}
      </Cell>
      <Cell css={LobbyListStyles.tableCellSmall}>
        <span css={LobbyListStyles.tableSubCell}>{lobby.status}</span>
      </Cell>
      <Cell>{connect_section}</Cell>
    </tr>
  );
};

function LobbyListView(props: {
  session: Session;
  lobbies: LobbyListItem[];
  onJoinLobby: (lobby: LobbyListItem) => void;
  onSpectateLobby: (lobby: LobbyListItem) => void;
  onCreateLobby: () => void;
  onTutorialStart: () => void;
}) {
  const {
    session,
    lobbies,
    onJoinLobby,
    onSpectateLobby,
    onCreateLobby,
    onTutorialStart,
  } = props;

  const user = session && session.getUser();
  const lobbyViews = lobbies.map((lobby) => {
    return (
      <LobbyListEntry
        key={lobby.id}
        lobby={lobby}
        sessionUserID={user && user.id}
        onJoin={onJoinLobby}
        onSpectate={onSpectateLobby}
      />
    );
  });
  const HeaderCell = (props: {
    className?: string;
    children: React.ReactNode;
  }) => (
    <td
      className={props.className}
      css={[LobbyListStyles.tableCell, LobbyListStyles.tableCellHeader]}
    >
      {props.children}
    </td>
  );

  return (
    <div css={LobbyListStyles.container}>
      <div css={LobbyListStyles.header}>
        <LobbyButton
          css={LobbyListStyles.createButton}
          onClick={onTutorialStart}
        >
          Tutorial
        </LobbyButton>
        <LobbyButton css={LobbyListStyles.createButton} onClick={onCreateLobby}>
          Create Lobby
        </LobbyButton>
      </div>
      <div css={LobbyListStyles.tableContainer}>
        <table css={LobbyListStyles.table}>
          <thead>
            <tr css={LobbyListStyles.tableHeader}>
              <HeaderCell>Lobby</HeaderCell>
              <HeaderCell>Host</HeaderCell>
              <HeaderCell css={LobbyListStyles.tableCellSmall}>
                Players
              </HeaderCell>
              <HeaderCell css={LobbyListStyles.tableCellSmall}>
                Created
              </HeaderCell>
              <HeaderCell css={LobbyListStyles.tableCellSmall}>
                Status
              </HeaderCell>
              <HeaderCell>Play</HeaderCell>
            </tr>
          </thead>
          <tbody>{lobbyViews}</tbody>
        </table>
      </div>
    </div>
  );
}

const LobbyListStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    color: ColorPalette.brightText,
  }),
  header: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',

    fontSize: 16,
    margin: 20,
    marginBottom: 5,
    padding: 5,
    gap: 10,
  }),
  createButton: css({
    margin: 0,
    padding: '10px 80px 10px 80px',
    fontSize: 16,
  }),
  tableContainer: css({
    margin: 20,
    marginTop: 0,
  }),
  table: css({
    width: '100%',
    backgroundColor: UIColorPalette.lobbyDarkBG,
  }),
  tableHeader: css({
    backgroundColor: UIColorPalette.lobbyDarkerBG,
    color: UIColorPalette.lobbyDarkText,
    borderBottom: `1px solid ${UIColorPalette.lobbyDarkBorder}`,
  }),
  tableRow: css({
    color: ColorPalette.brightText,
  }),
  tableCell: css({
    textAlign: 'center',
    paddingLeft: 20,
    paddingRight: 20,

    borderLeft: `1px solid ${UIColorPalette.lobbyDarkBorderLight}`,
    borderRight: `1px solid ${UIColorPalette.lobbyDarkBorderLight}`,
    borderBottom: `1px solid ${UIColorPalette.lobbyDarkBorderBottom}`,
  }),
  tableCellHeader: css({
    padding: 10,
    borderColor: UIColorPalette.lobbyDarkBorder,
  }),
  tableCellSmall: css({
    paddingLeft: 5,
    paddingRight: 5,
  }),
  tableSubCell: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
  }),
  tableCellHost: css({
    textAlign: 'left',
    paddingLeft: 5,
  }),
  hostName: css({
    textAlign: 'center',
  }),
  profile: css({
    height: 28,
    width: 28,
    margin: '5px 10px 5px 0px',
    verticalAlign: 'middle',
  }),
};

function MLobbyListView(props: {
  lobbies: LobbyListItem[];
  session: Session;
  onVisitLobby: (lobby: LobbyListItem) => void;
  onJoinLobby: (lobby: LobbyListItem) => void;
  onSpectateLobby: (lobby: LobbyListItem) => void;
  onCreateLobby: () => void;
}) {
  const {
    session,
    lobbies,
    onVisitLobby,
    onJoinLobby,
    onSpectateLobby,
    onCreateLobby,
  } = props;

  const lobbyViews = lobbies.map((lobby) => {
    return (
      <MLobbyListEntry
        key={lobby.id}
        lobby={lobby}
        sessionUserID={session.getUser()?.id}
        onVisit={onVisitLobby}
        onSpectate={onSpectateLobby}
        onJoin={onJoinLobby}
      />
    );
  });

  return (
    <div css={MLobbyListStyles.container}>
      <LobbyButton css={MLobbyListStyles.createButton} onClick={onCreateLobby}>
        Create Lobby
      </LobbyButton>
      {lobbyViews}
    </div>
  );
}
function MLobbyListEntry(props: {
  lobby: LobbyListItem;
  sessionUserID?: string;
  onVisit: (lobby: LobbyListItem) => void;
  onSpectate: (lobby: LobbyListItem) => void;
  onJoin: (lobby: LobbyListItem) => void;
}) {
  const { lobby, sessionUserID, onVisit, onSpectate, onJoin } = props;

  const sessionUserIsPlayer = lobby.players?.edges?.some(
    (edge) =>
      useFragment(UserSummaryFragmentDoc, edge?.node)?.id === sessionUserID,
  );

  const button = match(lobby)
    .with({ game: { endTimestamp: P.number } }, () => (
      <LobbyButton
        css={MLobbyListStyles.joinButton}
        onClick={() => onSpectate(lobby)}
        secondary={true}
      >
        Results
      </LobbyButton>
    ))
    .with({ game: { endTimestamp: P.nullish } }, () => {
      return (
        <LobbyButton
          css={MLobbyListStyles.joinButton}
          secondary={!sessionUserIsPlayer}
          onClick={() => onJoin(lobby)}
        >
          {sessionUserIsPlayer ? 'Resume' : 'Watch'}
        </LobbyButton>
      );
    })
    .with({ game: P.nullish }, () => (
      <LobbyButton
        css={MLobbyListStyles.joinButton}
        onClick={() => onJoin(lobby)}
      >
        Join
      </LobbyButton>
    ))
    .otherwise(() => {
      invariant(false, 'Invalid lobby');
    });

  return (
    <div css={MLobbyListStyles.entry} onClick={() => onVisit(lobby)}>
      <div css={MLobbyListStyles.row}>
        <div css={MLobbyListStyles.entryNameColumn}>
          {lobby.name}
          <div css={MLobbyListStyles.entryDate}>
            {timeAgo(lobby.creationDate)}
          </div>
        </div>

        <div css={MLobbyListStyles.statusColumn}>{lobby.status}</div>

        {button}
      </div>
      <div css={MLobbyListStyles.playerRow}>
        <MLobbyPlayer player={lobby.host} />
        {lobby.players?.edges?.map((edge) => {
          const player = useFragment(UserSummaryFragmentDoc, edge?.node);
          const host = useFragment(UserSummaryFragmentDoc, lobby.host);
          if (player?.id === host?.id) {
            return null;
          }
          return (
            <div key={player?.id} css={MLobbyListStyles.entryPlayer}>
              <MLobbyPlayer
                player={player}
                isSessionUser={player?.id === sessionUserID}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
function MLobbyPlayer(props: { player: any; isSessionUser?: boolean }) {
  const { player, isSessionUser } = props;
  return (
    <div css={MLobbyListStyles.entryPlayer}>
      <img
        src={player.profilePictureURL}
        css={MLobbyListStyles.entryHostProfile}
      />
      {isSessionUser ? '⭐' : ''}
      {player.name}
    </div>
  );
}
const MLobbyListStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',

    padding: 10,
    gap: 8,
  }),

  createButton: css({
    [MEDIA_QUERY_MOBILE]: {
      margin: 0,
      padding: 10,
      fontSize: 16,
    },
  }),

  entry: css({
    display: 'flex',
    flexDirection: 'column',

    backgroundColor: UIColorPalette.lobbyDarkBG,
    borderRadius: 4,

    padding: 8,

    gap: 8,
  }),
  entryNameColumn: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
  }),
  entryDate: css({
    fontSize: 12,
    color: ColorPalette.normalText,
  }),
  statusColumn: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 14,
    paddingTop: 4,
  }),
  row: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 4,
  }),

  playerRow: css({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  }),

  entryPlayer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    fontSize: 14,
    minWidth: 100,
  }),
  entryHostProfile: css({
    height: 16,
  }),

  joinButton: css({
    [MEDIA_QUERY_MOBILE]: {
      margin: 0,
      paddingTop: 12,
      paddingBottom: 12,
      minWidth: 100,
    },
  }),
};

const CREATE_LOBBY_MUTATION = graphql(`
  mutation CreateLobby {
    lobbyCreate {
      id
    }
  }
`);

function LobbyListPage() {
  const session = useSessionStore((state) => state.session)!;
  const { lobbyList, fetchLobbyList } = useLobbyStore(
    useShallow((state) => ({
      lobbyList: state.lobbyList,
      fetchLobbyList: state.fetchLobbyList,
    })),
  );

  const isMobile = useMobile();
  const navigate = useNavigate();
  const [requestInProgress, setRequestInProgress] = React.useState(false);

  useEffect(() => {
    fetchLobbyList();

    const subscription = subscribeToTopic(`lobby_list`, () => {
      useLobbyStore.getState().fetchLobbyList();
    });
    return () => {
      unsubscribeFromTopic(subscription);
    };
  }, [fetchLobbyList]);
  const onLobbyJoin = useCallback(
    async (lobby: LobbyListItem) => {
      var mutator = new LobbyMutator(session, lobby.id);
      await mutator.joinGame();
      navigate('/lobby/' + lobby.id);
    },
    [navigate, session],
  );
  const onLobbyVisit = useCallback(
    (lobby: LobbyListItem) => {
      navigate('/lobby/' + lobby.id);
    },
    [navigate],
  );
  const onLobbySpectate = useCallback(
    (lobby: LobbyListItem) => {
      if (!lobby.game) {
        return;
      }
      navigate('/game/' + lobby.game.id);
    },
    [navigate],
  );
  const onTutorialStart = useCallback(async () => {
    if (requestInProgress) {
      return;
    }
    setRequestInProgress(true);

    const result = await executeGQL(TUTORIAL_START_MUTATION);
    if (result.errors) {
      console.error(result.errors);
      alert('error starting tutorial ' + result.errors[0].message);
      setRequestInProgress(false);
      return;
    }
    navigate('/game/' + result.data?.tutorialStart?.id);
  }, [navigate]);
  const onCreateLobby = useCallback(async () => {
    if (requestInProgress) {
      return;
    }
    setRequestInProgress(true);

    const result = await executeGQL(CREATE_LOBBY_MUTATION);
    if (result.errors) {
      console.error(result.errors);
      alert('error creating lobby ' + result.errors[0].message);
      setRequestInProgress(false);
      return;
    }
    const lobby = result.data?.lobbyCreate;
    if (!lobby) {
      alert('error creating lobby');
      setRequestInProgress(false);
      return;
    }
    navigate('/lobby/' + lobby.id);
  }, [navigate, requestInProgress, setRequestInProgress]);

  if (!lobbyList) {
    return null;
  }

  if (isMobile) {
    return (
      <MLobbyListView
        lobbies={lobbyList}
        session={session}
        onVisitLobby={onLobbyVisit}
        onJoinLobby={onLobbyJoin}
        onSpectateLobby={onLobbySpectate}
        onCreateLobby={onCreateLobby}
      />
    );
  }

  return (
    <LobbyListView
      lobbies={lobbyList}
      session={session}
      onJoinLobby={onLobbyJoin}
      onSpectateLobby={onLobbySpectate}
      onCreateLobby={onCreateLobby}
      onTutorialStart={onTutorialStart}
    />
  );
}

const TUTORIAL_START_MUTATION = graphql(`
  mutation TutorialStart {
    tutorialStart {
      id
    }
  }
`);

export default LobbyListPage;
